import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

/* ============================================================
   Frame-by-frame presentation (same behaviour as the Meezaan site).
   [data-frame]      one frame: exactly one screen tall below the header
   [data-pg-item]    a piece the frame may be divided at when it does not fit
   [data-pg-head]    a heading kept on every page of a divided frame
   A wheel / swipe / key gesture moves exactly one stop; a divided frame
   contributes one stop per page and the next page slides in on the same frame.
   ============================================================ */

const SNAP_PATHS = new Set(["/", "/about", "/services", "/industries", "/portfolio", "/metamorphosis", "/innovation", "/insights", "/contact", "/privacy", "/terms"]);
const DURATION = 1.05;
const PAGE_LOCK = 620;
const COOLDOWN = 380;
const BAR = 44;
const SAFE = 8;
const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

function headerHeight() {
  const h = document.querySelector("[data-site-header]");
  return h ? h.getBoundingClientRect().height : 0;
}
function topFrames() {
  return Array.from(document.querySelectorAll("[data-frame]")).filter((el) => !el.parentElement.closest("[data-frame]"));
}
function viewHeight() { return Math.round(window.innerHeight - headerHeight()); }
function ownScroll(el, dir = 0) {
  const o = el && el.closest && el.closest("[data-own-scroll]");
  if (!o || o.scrollHeight <= o.clientHeight + 1) return false;
  if (dir > 0 && o.scrollTop + o.clientHeight >= o.scrollHeight - 1) return false;
  if (dir < 0 && o.scrollTop <= 0) return false;
  return true;
}

function isTyping(el) {
  if (!el) return false;
  const t = el.tagName;
  return t === "INPUT" || t === "TEXTAREA" || t === "SELECT" || el.isContentEditable;
}

function show(frame, items, set) { items.forEach((el) => el.classList.toggle("pg-off", !set.has(el))); }

function clear(frame) {
  frame.querySelectorAll(":scope > .pg-bar").forEach((b) => b.remove());
  frame.querySelectorAll(".pg-off,.pg-enter").forEach((el) => el.classList.remove("pg-off", "pg-enter"));
  frame.classList.remove("pg-cont");
  frame.removeAttribute("data-pages");
  frame.style.height = ""; frame.style.minHeight = ""; frame.style.paddingBottom = "";
  frame.__pg = null;
}

function packFrame(frame, view) {
  clear(frame);
  const items = Array.from(frame.querySelectorAll("[data-pg-item]")).filter((el) => {
    for (let p = el.parentElement; p && p !== frame; p = p.parentElement) if (p.hasAttribute("data-pg-item")) return false;
    return true;
  });
  frame.classList.add("pg-measure");
  const fits = frame.getBoundingClientRect().height <= view - 2;
  let pages = [[0, items.length - 1]];
  if (!fits && items.length > 1) {
    const cap = view - BAR - SAFE;
    const measure = (a, b, cont) => {
      frame.classList.toggle("pg-cont", cont);
      show(frame, items, new Set(items.slice(a, b + 1)));
      return frame.getBoundingClientRect().height;
    };
    const pack = (c) => {
      const out = [];
      let a = 0;
      while (a < items.length) {
        let b = a;
        while (b + 1 < items.length && measure(a, b + 1, a > 0) <= c) b += 1;
        out.push([a, b]);
        a = b + 1;
      }
      return out;
    };
    pages = pack(cap);
    if (pages.length > 1) {
      let lo = Math.floor(cap * 0.45), hi = cap;
      for (let k = 0; k < 7; k += 1) { const mid = Math.floor((lo + hi) / 2); if (pack(mid).length <= pages.length) hi = mid; else lo = mid + 1; }
      const balanced = pack(hi);
      if (balanced.length === pages.length) pages = balanced;
    }
  }
  frame.classList.remove("pg-measure");
  frame.__pg = { items, pages, page: 0 };
  frame.dataset.pages = String(pages.length);
  frame.style.height = view + "px";
  frame.style.minHeight = view + "px";
  if (pages.length > 1) {
    const pb = parseFloat(getComputedStyle(frame).paddingBottom) || 0;
    frame.style.paddingBottom = pb + BAR + "px";
    const bar = document.createElement("div");
    bar.className = "pg-bar";
    bar.setAttribute("aria-hidden", "true");
    const lbl = frame.querySelector("[data-pg-label]");
    bar.innerHTML = `<span class="pg-bar-lbl"></span><span class="pg-bar-dots">${pages.map(() => "<i></i>").join("")}</span><span class="pg-bar-num"></span>`;
    bar.querySelector(".pg-bar-lbl").textContent = lbl ? lbl.textContent.trim() : "";
    frame.appendChild(bar);
  }
  setPage(frame, 0, 0);
}

function setPage(frame, p, dir) {
  const pg = frame.__pg;
  if (!pg) return;
  const n = pg.pages.length;
  p = Math.max(0, Math.min(n - 1, p));
  pg.page = p;
  frame.classList.toggle("pg-cont", p > 0);
  const [a, b] = pg.pages[p];
  const set = new Set(pg.items.slice(a, b + 1));
  if (n > 1) show(frame, pg.items, set); else pg.items.forEach((el) => el.classList.remove("pg-off"));
  if (dir) {
    let i = 0;
    set.forEach((el) => { el.classList.remove("pg-enter"); void el.offsetWidth; el.style.setProperty("--pg-i", String(i)); el.style.setProperty("--pg-dir", String(dir)); el.classList.add("pg-enter"); i += 1; });
  }
  const bar = frame.querySelector(":scope > .pg-bar");
  if (bar) {
    bar.querySelector(".pg-bar-num").textContent = `${String(p + 1).padStart(2, "0")} / ${String(n).padStart(2, "0")}`;
    bar.querySelectorAll(".pg-bar-dots i").forEach((d, k) => d.classList.toggle("on", k === p));
  }
}

export const requestRepack = () => window.dispatchEvent(new Event("regera:repack"));

export function packFrames() {
  const view = viewHeight();
  topFrames().forEach((f) => packFrame(f, view));
}

export function getStops() {
  const hh = headerHeight();
  const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  const y0 = window.scrollY;
  const stops = [];
  topFrames().forEach((el, i) => {
    const r = el.getBoundingClientRect();
    const start = Math.max(0, Math.min(Math.round(i === 0 ? 0 : r.top + y0 - hh), maxY));
    const n = el.__pg ? el.__pg.pages.length : 1;
    for (let p = 0; p < n; p += 1) stops.push({ y: start, frame: el, page: p });
  });
  if (!stops.length) stops.push({ y: 0, frame: null, page: 0 });
  // the footer after the last frame is a final stop
  const last = stops[stops.length - 1];
  if (maxY - last.y > 6) stops.push({ y: maxY, frame: null, page: 0 });
  return stops;
}

function currentIndex(stops) {
  const y = window.scrollY;
  let best = 0;
  stops.forEach((s, i) => { if (Math.abs(s.y - y) < Math.abs(stops[best].y - y)) best = i; });
  const f = stops[best].frame;
  if (f && f.__pg) { const i = stops.findIndex((s) => s.frame === f && s.page === f.__pg.page); if (i >= 0) return i; }
  return best;
}

export function goToFrame(el) {
  if (!el) return;
  const s = getStops().find((x) => x.frame === el);
  const y = s ? s.y : Math.round(el.getBoundingClientRect().top + window.scrollY - headerHeight());
  if (window.__lenis) window.__lenis.scrollTo(y, { duration: DURATION, easing: ease, lock: true, force: true });
  else window.scrollTo({ top: y, behavior: "smooth" });
}

const LARGE = "(min-width:1024px) and (min-height:600px)";
function useLargeScreen() {
  const [ok, setOk] = useState(() => typeof window !== "undefined" && window.matchMedia(LARGE).matches);
  useEffect(() => {
    const mq = window.matchMedia(LARGE);
    const on = () => setOk(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return ok;
}

/* Phones and small tablets scroll naturally: content is never hidden to fit a frame. */
export function useFrameSnap() {
  const { pathname } = useLocation();
  const large = useLargeScreen();
  const enabled = large && SNAP_PATHS.has(pathname);
  useEffect(() => {
    const root = document.documentElement;
    if (!enabled) { root.classList.remove("snap-on"); return undefined; }
    root.classList.add("snap-on");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let animating = false, lastEnd = 0, lastWheel = 0, settle = null, packT = null, touch = null;
    let lastW = window.innerWidth, lastH = window.innerHeight;
    const timers = [];
    const finish = () => { animating = false; lastEnd = performance.now(); };
    const scrollToY = (y) => {
      if (Math.abs(y - window.scrollY) < 2) return;
      animating = true;
      const l = window.__lenis;
      if (l) { l.scrollTo(y, { duration: reduce ? 0 : DURATION, easing: ease, lock: true, force: true, immediate: reduce, onComplete: finish }); if (reduce) finish(); }
      else { window.scrollTo({ top: y, behavior: reduce ? "auto" : "smooth" }); setTimeout(finish, reduce ? 0 : DURATION * 1000); }
    };
    const goTo = (s, dir) => {
      if (s.frame && s.frame.__pg && Math.abs(s.y - window.scrollY) < 2) {
        if (s.frame.__pg.page === s.page) return;
        animating = true; setPage(s.frame, s.page, reduce ? 0 : dir); setTimeout(finish, reduce ? 0 : PAGE_LOCK); return;
      }
      if (s.frame && s.frame.__pg) setPage(s.frame, s.page, 0);
      scrollToY(s.y);
    };
    const step = (dir) => { const st = getStops(); const nx = st[currentIndex(st) + dir]; if (nx) goTo(nx, dir); };
    const menuOpen = () => !!document.querySelector("[data-menu-open='true']");
    const onWheel = (e) => {
      if (e.ctrlKey || menuOpen()) return;
      if (ownScroll(e.target, Math.sign(e.deltaY))) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      e.preventDefault(); e.stopPropagation();
      const now = performance.now(); const cont = now - lastWheel < 160; lastWheel = now;
      if (animating) return;
      if (now - lastEnd < COOLDOWN || (cont && now - lastEnd < 1400)) return;
      if (Math.abs(e.deltaY) < 3) return;
      step(e.deltaY > 0 ? 1 : -1);
    };
    const onTS = (e) => { if (e.touches.length !== 1 || menuOpen() || (isTyping(e.target) && e.target === document.activeElement)) { touch = null; return; } touch = { y: e.touches[0].clientY, x: e.touches[0].clientX, t: e.target, inner: false }; };
    const onTM = (e) => { if (!touch) return; const ry = e.touches[0].clientY - touch.y; if (touch.inner || ownScroll(touch.t, Math.sign(-ry))) { touch.inner = true; return; } const dy = Math.abs(ry), dx = Math.abs(e.touches[0].clientX - touch.x); if (dy > dx && e.cancelable) e.preventDefault(); };
    const onTE = (e) => { if (!touch) return; if (touch.inner) { touch = null; return; } const t = e.changedTouches[0]; const dy = touch.y - t.clientY, dx = touch.x - t.clientX; touch = null; if (Math.abs(dy) < 36 || Math.abs(dx) > Math.abs(dy)) return; if (animating || performance.now() - lastEnd < 200) return; step(dy > 0 ? 1 : -1); };
    const onKey = (e) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || isTyping(e.target) || menuOpen()) return;
      const k = e.key; let dir = 0;
      if (k === "ArrowDown" || k === "PageDown" || (k === " " && !e.shiftKey)) dir = 1;
      else if (k === "ArrowUp" || k === "PageUp" || (k === " " && e.shiftKey)) dir = -1;
      else if (k === "Home") { e.preventDefault(); goTo(getStops()[0], -1); return; }
      else if (k === "End") { e.preventDefault(); const s = getStops(); goTo(s[s.length - 1], 1); return; }
      if (!dir) return;
      if (k === " " && e.target.closest && e.target.closest("button, a, [role=button]")) return;
      e.preventDefault(); if (!animating) step(dir);
    };
    const onScroll = () => {
      if (animating) return;
      clearTimeout(settle);
      settle = setTimeout(() => { if (animating || touch) return; const st = getStops(); const s = st[currentIndex(st)]; if (s && Math.abs(s.y - window.scrollY) > 3) scrollToY(s.y); }, 240);
    };
    const repack = () => {
      if (isTyping(document.activeElement)) return;
      const st = getStops(); const cur = st[currentIndex(st)];
      const keep = cur && cur.frame, kp = keep && keep.__pg ? keep.__pg.page : 0;
      packFrames(); if (keep && keep.__pg) setPage(keep, kp, 0); onScroll();
    };
    const onResize = () => { const w = window.innerWidth, h = window.innerHeight; if (w === lastW && Math.abs(h - lastH) < 2) return; lastW = w; lastH = h; clearTimeout(packT); packT = setTimeout(repack, 220); };
    window.__regeraFrames = { setPage, getStops, packFrames };
    packFrames();
    [350, 1200].forEach((ms) => timers.push(setTimeout(repack, ms)));
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => timers.push(setTimeout(repack, 0)));
    document.addEventListener("wheel", onWheel, { passive: false, capture: true });
    document.addEventListener("touchstart", onTS, { passive: true });
    document.addEventListener("touchmove", onTM, { passive: false });
    document.addEventListener("touchend", onTE, { passive: true });
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    const onRepack = () => { clearTimeout(packT); packT = setTimeout(repack, 60); };
    window.addEventListener("regera:repack", onRepack);
    return () => {
      clearTimeout(settle); clearTimeout(packT); timers.forEach(clearTimeout);
      topFrames().forEach(clear);
      document.removeEventListener("wheel", onWheel, { capture: true });
      document.removeEventListener("touchstart", onTS); document.removeEventListener("touchmove", onTM); document.removeEventListener("touchend", onTE);
      window.removeEventListener("keydown", onKey); window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onResize); window.removeEventListener("regera:repack", onRepack);
      root.classList.remove("snap-on");
    };
  }, [enabled, pathname]);
}
